import React, {ReactNode, useEffect, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {lastCreatedDocumentId$} from 'models/documents/create';
import {
    filtersChanged,
    filtersReset,
    pageNumberChanged,
    refetchWithSortByDescDate,
    sortingsChanged,
    stateReset,
    tableState$,
    updOnlyChanged,
} from 'models/documents/documents/grid/table';
import {DocumentsGridFormattedRow, DocumentsGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction, showType: boolean) => Array<any>;
    dataMapper: (
        data: DocumentsGridRawRow[] | undefined,
        t: TFunction,
        lastCreatedDocumentId?: string | null,
        updOnly?: boolean,
    ) => DocumentsGridFormattedRow[];
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    headerControls?: ReactNode;
}

export const DocumentsGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const INVOICES = [
        ROUTE.DOCUMENTS_INVOICES,
        ROUTE.DOCUMENTS_INVOICES_INCOMING,
        ROUTE.DOCUMENTS_INVOICES_OUTGOING,
    ];
    updOnlyChanged(INVOICES.some((i) => location.pathname === i));
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings, updOnly} = useStore(tableState$);
    const lastCreatedDocumentId = useStore(lastCreatedDocumentId$);

    const INVOICE_PAGES: Record<string, string> = {
        [ROUTE.DOCUMENTS_INVOICES]: 'invoicesGrid',
        [ROUTE.DOCUMENTS_INVOICES_INCOMING]: 'invoicesGrid',
        [ROUTE.DOCUMENTS_INVOICES_OUTGOING]: 'invoicesGrid',
    };

    useEffect(() => {
        stateReset();
        refetchWithSortByDescDate();
    }, []);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, !updOnly),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );

    const mappedData = React.useMemo(
        () => dataMapper(data?.content, t, lastCreatedDocumentId, updOnly),
        [dataMapper, data, t, lastCreatedDocumentId],
    );

    return (
        <>
            <Table
                tableTabsSettings={
                    INVOICES.some((i) => i === location.pathname)
                        ? {
                              baseUrl: '/invoices',
                              links: [
                                  {label: t('pages.documents.invoices.incoming'), path: 'incoming'},
                                  {label: t('pages.documents.invoices.outgoing'), path: 'outgoing'},
                              ],
                          }
                        : undefined
                }
                columns={columns}
                data={mappedData}
                hasNext={data?.hasNext}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                userConfig={{
                    gridName: INVOICE_PAGES[location.pathname] ? 'invoicesGrid' : 'documentsGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                headerControls={headerControls}
                exportConfig={exportConfig}
            />
        </>
    );
};
